import { computed, onMounted, ref, watch } from "vue";
import { type LocationQueryRaw, useRoute, useRouter } from "vue-router";

import { DEFAULT_PAGE, DEFAULT_PER_PAGE } from "@/helpers/constants";
import { getActiveFilters } from "@/helpers/formatting";
import { getParamsWithPagination } from "@/helpers/pagination";

type Options<T> = {
  /**
   * Specify defaults for `page`, `per_page`, `sort`, and `search`. If no defaults are given here,
   * we use the following values
   *
   * - page: `1`
   * - per_page: `100`
   * - search: `""`
   * - sort: `""`
   */
  defaults?: FilterDefaults;

  /**
   * Additional filters to apply to the route when this composable mounts.
   */
  additionalFilters?: T;

  /**
   * Prevents applying filters to the route when the composable mounts.
   */
  preventSetFiltersOnMount?: boolean;
};

type FilterDefaults = {
  page?: number;
  per_page?: number;
  search?: string;
  sort?: string;
};

export const useFilters = <T extends Record<string, unknown>>(
  options?: Options<T>
) => {
  const defaults = options?.defaults;
  const additionalFilters = ref<T>(options?.additionalFilters ?? ({} as T));

  const route = useRoute();
  const router = useRouter();

  const searchTerm = ref(
    (route.query.search ?? options?.defaults?.search ?? "") as string
  );

  const filterState = computed(
    () =>
      ({
        page: Number(route.query.page ?? defaults?.page ?? DEFAULT_PAGE),
        per_page: Number(
          route.query.per_page ?? defaults?.per_page ?? DEFAULT_PER_PAGE
        ),
        search: route.query.search ?? "",
        sort: route.query.sort ?? "",
        ...additionalFilters.value,
      }) as FilterDefaults & T
  );

  const filterStateAsSearchParams = computed(() =>
    getParamsWithPagination(
      filterState.value,
      filterState.value.page as number,
      filterState.value.per_page as number
    )
  );

  const setFilter = async (filter: Partial<T>) => {
    additionalFilters.value = {
      ...additionalFilters.value,
      ...filter,
    };

    const sanitizedAdditionalFilters = getActiveFilters(
      additionalFilters.value as Record<string, unknown>
    );

    await router.push({
      query: {
        ...route.query,
        ...(sanitizedAdditionalFilters as LocationQueryRaw),
      },
    });
  };

  const changePage = async (page: number) => {
    await router.push({
      query: {
        ...route.query,
        page,
      },
    });
  };

  const changeItemsPerPage = async (itemsPerPage: number) => {
    await router.push({
      query: {
        ...route.query,
        per_page: itemsPerPage,
      },
    });
  };

  watch(searchTerm, async (newSearchTerm) => {
    await router.push({
      query: {
        ...route.query,
        search: newSearchTerm,
      },
    });
  });

  onMounted(async () => {
    if (options?.preventSetFiltersOnMount) {
      return;
    }

    const sanitizedFilters = getActiveFilters({
      ...route.query,
      page: route.query.page ?? defaults?.page ?? DEFAULT_PAGE,
      per_page: route.query.per_page ?? defaults?.per_page ?? DEFAULT_PER_PAGE,
      sort: route.query.sort ?? defaults?.sort,
      search: route.query.search ?? searchTerm.value ?? undefined,
      ...(additionalFilters.value as Record<string, unknown>),
    });

    await router.push({
      query: {
        ...route.query,
        ...(sanitizedFilters as LocationQueryRaw),
      },
    });
  });

  return {
    searchTerm,
    filterState,
    filterStateAsSearchParams,
    changePage,
    changeItemsPerPage,
    setFilter,
  };
};
